import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { authApi, getToken, getStoredUser, clearToken, type FirmUser } from '../lib/api';

interface AuthContextValue {
  user: FirmUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Optimistic initial identity from the cached user, for an instant paint.
  // This is COSMETIC ONLY: ProtectedRoute waits for isLoading to clear before
  // it ever trusts isAuthenticated, so a stale cached user can never grant
  // access — verify() against /api/auth/me below is the real gate (Wisdom §4).
  const [user, setUser] = useState<FirmUser | null>(() => getStoredUser());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      if (!getToken()) {
        if (!cancelled) {
          setUser(null);
          setIsLoading(false);
        }
        return;
      }
      try {
        const { user: verified } = await authApi.verify();
        if (!cancelled) setUser(verified);
      } catch {
        // verify() clears the token + redirects on a hard failure; null the
        // user here too so the UI never trusts a stale identity.
        if (!cancelled) {
          clearToken();
          setUser(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void checkSession();
    return () => {
      cancelled = true;
    };
  }, []);

  async function login(email: string, password: string): Promise<void> {
    setIsLoading(true);
    try {
      const { user: loggedIn } = await authApi.login(email, password);
      setUser(loggedIn);
    } finally {
      setIsLoading(false);
    }
  }

  function logout(): void {
    authApi.logout();
    setUser(null);
  }

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
